import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
    Checkbox,
    TextField,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    Card,
    Button,
    MenuItem,
    CircularProgress,
} from "@mui/material";

import { Cancel as CancelIcon, Save as SaveIcon } from "@mui/icons-material";

import { Toolbar } from "react-admin";

function V3BatchForm({ onSave, saving, onCancel }) {
    const [inputs, setInputs] = useState({
        // Matching API instead of adhering to camel case here
        preheat_interval: true,
        server_url: true,
        server_contact_interval: true,
        compressed_scan_interval: true,
        raw_scan_interval: true,
        numUdp: true,
        maxUdp: true,
        pointCountMax: true,
        low_power_mode: true,
        priority_group: true,
    });

    const [payload, setPayload] = useState({});

    const [preheatInterval, setPreheatInterval] = useState("");
    const [serverUrl, setServerUrl] = useState("");
    const [serverContactInterval, setServerContactInterval] = useState("");
    const [compressedScanInterval, setCompressedScanInterval] = useState("");
    const [rawScanInterval, setRawScanInterval] = useState("");
    const [udp, setUdp] = useState("");
    const [maxUdp, setMaxUdp] = useState("");
    const [pointCountMax, setPointCountMax] = useState("");
    const [lowPowerMode, setLowPowerMode] = useState("");
    const [priorityGroup, setPriorityGroup] = useState("");

    useEffect(() => {
        // Clear the payload for values in inputs which are false
        // TODO: Clear the form
        setPayload((prevPayload) => {
            let newPayload = { ...prevPayload };
            Object.keys(inputs).forEach((key) => {
                if (inputs[key] === true && key in newPayload) {
                    delete newPayload[key];
                }
            });
            return newPayload;
        });
    }, [inputs]);

    const toggleDisable = (id) => {
        setInputs((prevInputs) => ({
            ...prevInputs,
            [id]: !prevInputs[id],
        }));
    };

    const handleMouseChange = (click) => {
        setPayload((payload) => ({
            ...payload,
            [click.target.name]: click.target.value,
        }));
    };

    const handleChange = (event) => {
        setPayload((payload) => ({
            ...payload,
            // all non-mouse changes are number types; needs to be changed
            // if any text fields are added that aren't number types
            [event.target.name]: _.toNumber(event.target.value),
        }));
    };

    const invokeOnSave = () =>
        onSave({
            partial_config: { ...payload },
        });

    return (
        <Card>
            <Typography style={{ margin: 12 }} variant="h6">
                Settings
            </Typography>

            <Divider />

            <Typography style={{ margin: 15 }} variant="h6">
                Preheat (OS1 and VLP)
            </Typography>

            <TextField
                style={{ width: "75%", margin: 15 }}
                id="preheat_interval"
                name="preheat_interval"
                required={inputs["preheat_interval"]}
                disabled={inputs["preheat_interval"]}
                label="Preheat Interval"
                type="number"
                value={preheatInterval}
                onChange={(event) => {
                    handleChange(event);
                    setPreheatInterval(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("preheat_interval");
                }}
            />

            <Typography style={{ margin: 15 }} variant="h6">
                Server Settings
            </Typography>

            <FormControl
                style={{ width: "90%", marginLeft: 15 }}
                id="server_url"
                disabled={inputs["server_url"]}
                variant="filled"
            >
                <InputLabel htmlFor="server-url-select">Server URL*</InputLabel>
                <Select
                    // native
                    value={serverUrl}
                    onChange={(e) => {
                        handleMouseChange(e);
                        setServerUrl(e.target.value);
                    }}
                    inputProps={{
                        name: "server_url",
                        id: "server-url-select",
                    }}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    <MenuItem value="https://dev-vpg-devices.linevisioninc.com">
                        (DEV VPG) https://dev-vpg-devices.linevisioninc.com
                    </MenuItem>
                    <MenuItem value="https://devices.linevisioninc.com/v3_dev">
                        (DEV) https://devices.linevisioninc.com/v3_dev
                    </MenuItem>
                    <MenuItem value="https://prod-vpg-devices.linevisioninc.com">
                        (PROD VPG) https://prod-vpg-devices.linevisioninc.com
                    </MenuItem>
                    <MenuItem value="https://devices.linevisioninc.com/v3.0.2">
                        (PROD) https://devices.linevisioninc.com/v3.0.2
                    </MenuItem>
                    <MenuItem value="https://devices.linevisioninc.com/v3.0.1">
                        (PROD) https://devices.linevisioninc.com/v3.0.1
                    </MenuItem>
                    <MenuItem value="https://devices.linevision.co/v3_dev">
                        (LEGACY) https://devices.linevision.co/v3_dev
                    </MenuItem>
                    <MenuItem value="https://devices.linevision.co/v3.0.1">
                        (LEGACY) https://devices.linevision.co/v3.0.1
                    </MenuItem>
                    <MenuItem value="https://devices.linevision.co/v3.0.1">
                        (LEGACY) https://devices.linevision.co/v3.0.2
                    </MenuItem>
                    <MenuItem value="https://devices.linevision.co/v3_test">
                        (LEGACY) https://devices.linevision.co/v3_test
                    </MenuItem>
                </Select>
            </FormControl>

            <Checkbox
                onClick={() => {
                    toggleDisable("server_url");
                }}
            />

            <Typography style={{ marginLeft: 15 }} variant="h6">
                Scan Frequency
            </Typography>

            <FormControl
                style={{ width: "90%", marginLeft: 15 }}
                id="low_power_mode"
                disabled={inputs["low_power_mode"]}
                variant="filled"
            >
                <InputLabel htmlFor="low_power_mode_select">Low Power Mode*</InputLabel>
                <Select
                    // native
                    value={lowPowerMode}
                    onChange={(e) => {
                        handleMouseChange(e);
                        setLowPowerMode(e.target.value);
                    }}
                    inputProps={{
                        name: "low_power_mode",
                        id: "low_power_mode_select",
                    }}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    <MenuItem value="on">(Sleep Mode ON)</MenuItem>
                    <MenuItem value="off">(Sleep Mode OFF)</MenuItem>
                </Select>
            </FormControl>

            <Checkbox
                onClick={() => {
                    toggleDisable("low_power_mode");
                }}
            />

            <TextField
                style={{ width: "75%", margin: 15 }}
                id="compressed_scan_interval"
                name="compressed_scan_interval"
                required={inputs["compressed_scan_interval"]}
                label="Compressed Scan - Scan Interval"
                disabled={inputs["compressed_scan_interval"]}
                value={compressedScanInterval}
                type="number"
                onChange={(event) => {
                    handleChange(event);
                    setCompressedScanInterval(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("compressed_scan_interval");
                }}
            />

            <Typography style={{ margin: 15 }} variant="h6">
                VLP Scan Time Parameters
            </Typography>

            <TextField
                style={{ width: "75%", marginLeft: 15 }}
                id="numUdp"
                name="numUDP"
                required={inputs["numUdp"]}
                disabled={inputs["numUdp"]}
                label="UDP"
                type="number"
                value={udp}
                onChange={(event) => {
                    handleChange(event);
                    setUdp(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("numUdp");
                }}
            />

            <TextField
                style={{ width: "75%", margin: 15 }}
                id="maxUdp"
                name="maxUDP"
                required={inputs["maxUdp"]}
                disabled={inputs["maxUdp"]}
                label="Max UDP"
                type="number"
                value={maxUdp}
                onChange={(event) => {
                    handleChange(event);
                    setMaxUdp(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("maxUdp");
                }}
            />

            <TextField
                style={{ width: "75%", margin: 15 }}
                id="pointCountMax"
                name="pointCountMax"
                required={inputs["pointCountMax"]}
                disabled={inputs["pointCountMax"]}
                label="Max Point Count"
                type="number"
                value={pointCountMax}
                onChange={(event) => {
                    handleChange(event);
                    setPointCountMax(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("pointCountMax");
                }}
            />

            <Typography style={{ margin: 15 }} variant="h6">
                Firmware Provision
            </Typography>

            <TextField
                style={{ width: "75%", marginLeft: 15 }}
                id="priority_group"
                name="priority_group"
                required={inputs["priority_group"]}
                disabled={inputs["priority_group"]}
                label="Priority Group"
                type="number"
                value={priorityGroup}
                onChange={(event) => {
                    handleChange(event);
                    setPriorityGroup(event.target.value);
                }}
            />

            <Checkbox
                onClick={() => {
                    toggleDisable("priority_group");
                }}
            />

            <Toolbar>
                <Button disabled={saving} variant="contained" onClick={onCancel} classes={{ root: "mr-4" }}>
                    {saving ? <CircularProgress size={18} thickness={2} /> : <CancelIcon />} Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={saving || Object.values(inputs).every((v) => v === true)}
                    onClick={invokeOnSave}
                >
                    {saving ? <CircularProgress size={18} thickness={2} /> : <SaveIcon />} Save
                </Button>
            </Toolbar>
        </Card>
    );
}

V3BatchForm.propTypes = {
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    saving: PropTypes.bool,
};

export { V3BatchForm };
